/* eslint-disable consistent-return */
/* eslint-disable import/export */
import axios, { AxiosError } from 'axios';
import useNotification from 'hooks/notification';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IResponse } from 'services/api/axios';
import i18n from 'services/i18n';

export type AsyncFetchError<T = undefined> = {
  error: AxiosError
  errMessages: string[]
  errMessage: string
  params: T
};

export default function useAsyncFetch<T extends any[], K>(
  props: {
    asyncFunc: (...params: T) => Promise<void | K>,
    initialState?: K
    successHandler?: (state?: K, params?: T) => K | void,
    errorHandler?: (errorObj: AsyncFetchError<T>) => void,
    errorNote?: boolean,
    successNote?: string,
    customErrorMessage?: string
    disableRedirectOn401?: boolean
  },
): [(...params: T) => Promise<K>, boolean, K, React.Dispatch<React.SetStateAction<K>>];

export default function useAsyncFetch<K>(
  props: {
    asyncFunc: () => Promise<void | K>,
    initialState?: K
    successHandler?: (state?: K) => void,
    errorHandler?: (errorObj: AsyncFetchError) => void,
    errorNote?: boolean,
    successNote?: string,
    immediate?: boolean
    customErrorMessage?: string
    disableRedirectOn401?: boolean
  },
): [() => Promise<K>, boolean, K, React.Dispatch<React.SetStateAction<K>>];

export default function useAsyncFetch<T extends any[], K>(
  props: {
    asyncFunc: (...params: T) => Promise<void | K>,
    initialState?: K
    successHandler?: (state?: K, params?: T) => K | void,
    errorHandler?: (errorObj: AsyncFetchError<T>) => void,
    errorNote?: boolean,
    successNote?: string,
    immediate: boolean,
    initialParams: T
    customErrorMessage?: string
    disableRedirectOn401?: boolean
  },
): [(...params: T) => Promise<K>, boolean, K, React.Dispatch<React.SetStateAction<K>>];

export default function useAsyncFetch({
  asyncFunc,
  initialState,
  successHandler,
  errorHandler,
  errorNote,
  successNote,
  immediate,
  initialParams,
  customErrorMessage,
  disableRedirectOn401,
}: any) {
  const [loading, setLoading] = React.useState<boolean>(!!immediate);
  const [state, setState] = React.useState(initialState);
  const mountedRef = React.useRef(true);
  const notification = useNotification();
  const navigate = useNavigate();

  const execute = React.useCallback((...params) => {
    setLoading(true);
    return asyncFunc(...params)
      .then((res: any) => {
        if (!mountedRef.current) return;

        if (successNote) {
          notification.success('', successNote);
        }

        if (successHandler) {
          successHandler(res, params as any);
        }
        if (initialState !== undefined) {
          setState(res);
        }
        return res;
      })
      .catch((err: any) => {
        if (!mountedRef.current) return;
        if (axios.isAxiosError(err) && err.response?.status === 401 && !disableRedirectOn401) {
          navigate('/');
          return;
        }

        const errors = err.response?.data?.errors as IResponse['errors'];

        if (errors?.length && errorNote) {
          errors.forEach((errorObj) => {
            notification.error(errorObj.message || i18n.t('form.error.error_500'));
          });
        } else if (errorNote) {
          notification.error(i18n.t('form.error.error_500'));
        }

        if (errorHandler) {
          errorHandler({
            error: err,
            errMessages: errors?.map((errorObj) => errorObj.message) || [],
            errMessage: errors?.map((errorObj) => errorObj.message).join(', ') || customErrorMessage || i18n.t('notification.error'),
            params,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [asyncFunc, initialParams]);

  React.useEffect(() => {
    if (immediate) {
      if (initialParams) {
        execute(...initialParams);
      } else execute();
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return [
    execute,
    loading,
    state,
    setState,
  ];
}
